import { useCallback, useContext, useEffect, useRef } from 'react';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from './useGetMixpanelProps';
import { useLocation } from 'react-router-dom';
import { PageViewedProps } from 'models/MixpanelEvents';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import credentialsService from 'services/credentialsService';
import useGetPageTitle from './useGetPageTitle';
import useFirst from './useFirst';
import {
  ADMIN_DASHBOARD_ROUTE,
  AUTH_ROUTE,
  DATA_ROOM_ROUTE,
  PORTFOLIO_ROUTE,
  BASE_PROFILE_ROUTE,
  CONTACTS_ROUTE,
  MESSAGING_ROUTE,
  NOTIFICATIONS_ROUTE,
  ENTERPRISE_ROUTE,
  ONBOARD_ENTERPRISE_ROUTE,
  RWC_ROUTE,
  CONFIDENTIALITY_AGREEMENT_ROUTE,
  INVESTOR_VERIFICATION_ROUTE,
} from 'data/global';

const LOGGED_IN_PAGES = [
  DATA_ROOM_ROUTE,
  PORTFOLIO_ROUTE,
  ADMIN_DASHBOARD_ROUTE,
  BASE_PROFILE_ROUTE,
  CONTACTS_ROUTE,
  MESSAGING_ROUTE,
  NOTIFICATIONS_ROUTE,
  ENTERPRISE_ROUTE,
  CONFIDENTIALITY_AGREEMENT_ROUTE,
  INVESTOR_VERIFICATION_ROUTE,
  RWC_ROUTE,
  RWC_ROUTE.toLowerCase(),
];

const LOGGED_OUT_PAGES = [AUTH_ROUTE, ONBOARD_ENTERPRISE_ROUTE];

const useMixpanelPageEvent = () => {
  const { currentUser } = useContext(CurrentUserContext);

  const { pathname } = useLocation();

  const getMixpanelProps = useGetMixpanelProps();

  const getPageTitle = useGetPageTitle();

  const { onFirst: onFirstRender, resetFirst: resetFirstRender } = useFirst();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkPathname = useCallback(
    (route: string) => pathname.includes(route),
    [pathname],
  );

  useEffect(() => {
    resetFirstRender();
    // Clear any pending timeout when pathname changes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [pathname, resetFirstRender]);

  return useCallback(() => {
    if (!!credentialsService.token && !currentUser?._id) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    onFirstRender(() => {
      if (!currentUser && LOGGED_IN_PAGES.some(checkPathname)) return;

      if (currentUser && LOGGED_OUT_PAGES.some(checkPathname)) return;

      const page = getPageTitle();

      if (!page) return;

      // Set timeout to call pageViewed after 1 second
      timeoutRef.current = setTimeout(() => {
        mixpanelService.pageViewed(
          getMixpanelProps({ page }) as PageViewedProps,
        );
      }, 1000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, getMixpanelProps, getPageTitle, onFirstRender, checkPathname]);
};

export default useMixpanelPageEvent;
